import {
  type DataSecurityPolicy,
  type Policies,
  type PoliciesBody,
} from '@canvas/frame-api-types';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import {
  captureMessage,
  CustomerImpact,
  EventClassification,
} from '@canvas/sentry-react';

type ValidationResult<T> =
  | {
      valid: true;
      body: T;
    }
  | { valid: false; error: string; type: string };

const validatePoliciesBody = (body: any | undefined): ValidationResult<Policies> => {
  if (!body || typeof body !== 'object') {
    return {
      valid: false,
      error: 'policies is not an object',
      type: body === null ? 'null' : typeof body,
    };
  }

  if (!body.dataSecurityPolicy || typeof body.dataSecurityPolicy !== 'object') {
    return {
      valid: false,
      error: 'policies.dataSecurityPolicy is not an object',
      type: body === null ? 'null' : typeof body.dataSecurityPolicy,
    };
  }

  const policy = body.dataSecurityPolicy;
  // Falsy "DSP-allowed?" evaluations will be coerced to explicit "false"
  return {
    valid: true,
    body: {
      dataSecurityPolicy: {
        pageExport: Boolean(policy?.pageExport === true),
        attachmentDownload: Boolean(policy?.attachmentDownload === true),
      },
    },
  };
};

export const validatePoliciesAndTransformToDataSecurityPolicy = (
  policies: PoliciesBody | undefined,
): DataSecurityPolicy | undefined => {
  const result = validatePoliciesBody(policies);
  if (!result.valid) {
    const errorMessage =
      'Policies response is missing data that was expected to be present by the policies schema';

    ReliabilityMetrics.getInstance().failure(MetricName.POLICIES_SCHEMA_VALIDATION, {
      errorMessage,
    });

    captureMessage(errorMessage, {
      contexts: {
        'Policies response': policies,
        'Policies schema validation error': {
          error: result.error,
          policies,
          type: result.type,
        },
      },
      tags: {
        captureLocation:
          'validatePoliciesAndTransformToDataSecurityPolicy-invalid-schema',
        customerImpact: CustomerImpact.CUSTOMERS,
        eventClassification: EventClassification.BUG,
      },
      level: 'error',
    });
  } else {
    ReliabilityMetrics.getInstance().success(MetricName.POLICIES_SCHEMA_VALIDATION);
    return result.body.dataSecurityPolicy;
  }
};
